/**
 * SuggestionsSection - Suggested rewrites with modern styling
 *
 * Extracted from ModernResultsFull.tsx for Phase 3 Mobile Optimization.
 * Supports truncation (Phase 3.3) and accessibility.
 */

import { View, Pressable, StyleSheet } from 'react-native';
import { TierBody, TierHeadline } from '@dramadetector/ui';
import type { SuggestedRewrite } from '@dramadetector/shared';

import { captureEvent } from '../../../lib/analytics';
import { useSectionTruncation, TRUNCATION_DEFAULTS } from '../hooks';
import { sharedStyles } from '../sharedStyles';

interface SuggestionItemProps {
  rewrite: SuggestedRewrite;
  isLast: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/**
 * Individual suggestion item display
 */
function SuggestionItem({ rewrite, isLast, t }: SuggestionItemProps) {
  return (
    <View style={[sharedStyles.suggestionItem, isLast && sharedStyles.suggestionItemLast]}>
      {/* Original */}
      <View style={[sharedStyles.snippetBox, sharedStyles.beforeBox]}>
        <TierBody size="xs" color="text-gray-500" style={{ marginBottom: 4 }}>
          {t('home.modern.original', { defaultValue: 'Original:' })}
        </TierBody>
        <TierBody size="sm" color="text-gray-700">
          "{rewrite.original}"
        </TierBody>
      </View>

      {/* Rewritten */}
      <View style={[sharedStyles.snippetBox, sharedStyles.afterBox, { marginTop: 8 }]}>
        <TierBody size="xs" color="text-gray-500" style={{ marginBottom: 4 }}>
          {t('home.modern.suggested', { defaultValue: 'Suggested:' })}
        </TierBody>
        <TierBody size="sm" color="text-gray-700">
          "{rewrite.rewritten}"
        </TierBody>
      </View>

      {/* Reasoning */}
      {rewrite.reasoning && (
        <TierBody size="xs" color="text-gray-500" style={{ marginTop: 8, fontStyle: 'italic' }}>
          {rewrite.reasoning}
        </TierBody>
      )}
    </View>
  );
}

export interface SuggestionsSectionProps {
  /** List of rewrites to display */
  rewrites: SuggestedRewrite[];
  /** Translation function */
  t: (key: string, options?: Record<string, unknown>) => string;
  /** Test ID for testing */
  testID?: string;
  /** Maximum items before truncation (default: 2) */
  maxVisible?: number;
}

/**
 * Suggestions section with header and list
 * Supports truncation with "Show more/less" toggle
 */
export function SuggestionsSection({
  rewrites,
  t,
  testID,
  maxVisible = TRUNCATION_DEFAULTS.suggestions,
}: SuggestionsSectionProps) {
  const { showAll, toggle, isTruncated, visibleCount, hiddenCount } = useSectionTruncation({
    totalItems: rewrites.length,
    maxVisible,
  });

  if (!rewrites || rewrites.length === 0) {
    return null;
  }

  const visibleRewrites = showAll ? rewrites : rewrites.slice(0, visibleCount);

  const handleToggle = () => {
    captureEvent('Section_Toggle', {
      section: 'suggestions',
      action: showAll ? 'collapse' : 'expand',
      total_items: rewrites.length,
      visible_items: visibleCount,
    });
    toggle();
  };

  return (
    <View style={sharedStyles.glassCard} testID={testID}>
      <View style={sharedStyles.sectionHeader}>
        <TierHeadline size="lg" weight="semibold" color="text-gray-800" as="h3">
          {t('home.modern.suggested_improvements', { defaultValue: 'Suggested Improvements' })}
        </TierHeadline>
      </View>
      {visibleRewrites.map((rewrite, index) => (
        <SuggestionItem
          key={`rewrite-${index}`}
          rewrite={rewrite}
          isLast={index === visibleRewrites.length - 1 && !isTruncated}
          t={t}
        />
      ))}
      {/* Show more/less toggle */}
      {isTruncated && (
        <Pressable
          onPress={handleToggle}
          style={styles.toggleButton}
          accessibilityRole="button"
          accessibilityLabel={
            showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })
          }
          testID={`${testID}-toggle`}
        >
          <TierBody size="sm" style={styles.toggleText}>
            {showAll
              ? t('home.modern.show_less', { defaultValue: 'Show less' })
              : t('home.modern.show_more', { defaultValue: 'Show {count} more', count: hiddenCount })}
          </TierBody>
        </Pressable>
      )}
    </View>
  );
}

/**
 * StyleSheet - WCAG AAA compliant
 * - Touch target: min 44x44 points
 * - Contrast: purple-800 #5B21B6 on white = 9.4:1
 */
const styles = StyleSheet.create({
  toggleButton: {
    paddingVertical: 14,
    minHeight: 44, // WCAG touch target
    alignItems: 'center',
    justifyContent: 'center',
  },
  toggleText: {
    color: '#5B21B6', // purple-800 (WCAG AAA: 9.4:1 contrast)
    fontWeight: '600',
  },
});
